<template lang="pug">
  component(:is="embedded ? 'div' : 'DrawerContents'" ref="root")
    Modal(v-if="modal === 'delete'" @close="modal = null" :header="__('Delete Registry')")
      h4 {{__('Are you sure you want to delete this registry?')}}
      p {{__('This action cannot be undone')}}
      .flex
        RogueButton(color="gray" @click="modal = null") {{__('Cancel')}}
        RogueButton(color="red" :loading="deleting" @click="removeRegistry()") {{__('Delete Registry')}}
    h5 {{__('Registry Information')}}
    RogueInput(:required="true" label="Registry Name" v-model="event.name" :placeholder="__('E.g. John and Jane Doe\\'s Wedding')")
    Date(:required="true" label="Event Date" :dateType="'future'" v-model="event.date")
    RogueTextarea(:required="true" label="Description" v-model="event.description")
    Swatches(
      label="Privacy Status"
      :required="true"
      :options="[{label: 'Private', value: 'private'},{label: 'Public', value: 'public'}]"
      @update:modelValue="event.isPublic = $event === 'public'"
      :modelValue="event.isPublic ? 'public' : 'private'"
    )
    Checkbox.password-check#password-protected-checkbox(v-if="event.isPublic" v-model="event.hasPassword" label="Password Protected")
    RogueInput(
      v-if="event.isPublic && event.hasPassword"
      label="Registry Password"
      :placeholder="netHasPass ? 'Password set - Type here to change...' :''"
      v-model="event.password"
    )
    .extra-recipient
      p.font-size-xs(v-html="__('You will receive an email to <strong>{email}</strong> when an item on your registry is purchased.', {email: (event.recipients[0]  || {}).email || ''})")
      Checkbox.password-check#add-receipt-checkbox(v-model="hasAdditionalRecipient" label="Add Additional Recipient")
      .additional-recipient(v-if="hasAdditionalRecipient")
        RogueInput(:required="__('Please enter a first name')" label="First Name" v-model="event.recipients[1].firstName")
        RogueInput(:required="__('Please enter a last name')"  label="Last Name" v-model="event.recipients[1].lastName")
        RogueInput(:required="__('Please enter a valid email address')" type="email" label="Recipient Email Address" v-model="event.recipients[1].email")
    RogueSelect(:required="true" label="Shipping Address" :options="shipping" @update:modelValue="selectShipping($event)" v-model="addressId")
    AddressForm(v-if="addressId === 'new' && event.shipping && !event.shipping._id" v-model="event.shipping")

    .bottom(v-if="embedded")
      RogueButton.delete(width="full" @click="modal = 'delete'" v-if="editListId") {{__('Delete Registry')}}
      RogueButton.save(color="black" :disabled="!valid" width="full" @click="create()" :loading="loading")
        | {{editListId ? __('Save Registry') : __('Create Registry')}}
    template(#bottom)
      .bottom(v-if="!embedded")
        RogueButton.delete(width="full" @click="modal = 'delete'" v-if="editListId") {{__('Delete Registry')}}
        RogueButton.save(color="black" :disabled="!valid" width="full" @click="create()" :loading="loading")
          | {{editListId ? __('Save Registry') : __('Create Registry')}}
</template>
<script setup lang="ts">
import { computed, onMounted, ref, type ComponentPublicInstance } from 'vue'
import { useRouter } from 'vue-router'
import { validate } from '@/js/helpers/validate-fns'
import { useConfigStore } from '@/js/stores/configStore'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useListStore } from '@/js/stores/listStore'
import { useRogue } from '@/js/services/Rogue'
import type { Address, DisplayAddress } from '@/js/services/RogueTypes'
import type { List } from '@/js/stores/listTypes'

type RegistryRecipient = {
  firstName: string,
  lastName: string,
  phone?: string,
  email: string,
}

// The registry metadata this form owns - saved as list.metadata.registry
type RegistryEvent = {
  name: string,
  date: string,
  description: string,
  isPublic: boolean,
  hasPassword: boolean,
  password?: string,
  recipients: RegistryRecipient[],
  shipping: DisplayAddress | null,
}

const props = withDefaults(defineProps<{
  editListId?: string | null,
  embedded?: boolean,
}>(), {
  editListId : null,
  embedded   : false,
})

const emit = defineEmits<{
  save: [list: { _id?: string }],
  delete: [],
}>()

const configStore = useConfigStore()
const customerStore = useCustomerStore()
const listStore = useListStore()
const router = useRouter()
const rogue = useRogue()

// The root is a dynamic component: a plain element when embedded, DrawerContents otherwise
const root = ref<HTMLElement | ComponentPublicInstance | null>(null)

const addressId = ref('')
const valid = ref(true)
const loading = ref(false)
const modal = ref<string | null>(null)

const deleting = ref(false)

// Set to True if the network data object already has a password set. Passwords are not transmitted, and they could change it locally
const netHasPass = ref(false)

const hasAdditionalRecipient = ref(false)

const event = ref<RegistryEvent>({
  name        : '',
  date        : '',
  description : '',
  isPublic    : false,
  hasPassword : false,
  password    : '',
  recipients  : [{
    firstName : '',
    lastName  : '',
    phone     : '',
    email     : '',
  }, {
    firstName : '',
    lastName  : '',
    email     : '',
  }],
  shipping: null,
})

const addresses = computed(() => customerStore.addresses)
const firstname = computed(() => customerStore.firstname)
const lastname = computed(() => customerStore.lastname)
const email = computed(() => customerStore.email)
const registries = computed(() => listStore.registries)
const defaultAddress = computed(() => configStore.defaultAddress(rogue))

const shipping = computed(() => addresses.value.map(a => ({
  label : `${a.firstname} ${a.lastname} ${a.street.join(' ')} ${a.city} ${a.region} ${a.postcode}`,
  value : a._id,
})).concat([{
  label : 'Add New Address',
  value : 'new',
}]))

const editList = computed<List | undefined>(() => {
  if (!props.editListId) {
    return undefined
  }

  return registries.value.find(r => r._id === props.editListId)
})

// Resolve the validation container: the ref is the element itself when embedded,
// and the DrawerContents instance otherwise
function rootEl(): HTMLElement | undefined {
  const el = root.value

  if (!el) {
    return undefined
  }

  return ('$el' in el ? el.$el : el) as HTMLElement
}

function selectShipping(id: string) {
  if (id === 'new') {
    event.value.shipping = JSON.parse(JSON.stringify(defaultAddress.value))
  } else {
    event.value.shipping = addresses.value.find(a => a._id === id) || null
  }
}

async function removeRegistry() {
  deleting.value = true
  try {
    await listStore.deleteList(rogue, props.editListId as string)
    emit('delete')
    await router.push('/account/registry')
  } catch (e) {
    rogue.log.warn('Error deleting registry', e)
  }
  deleting.value = false
}

async function create() {
  const el = rootEl()
  if (!el || !validate(el)) {
    return
  }
  loading.value = true

  // Save the address if it doesnt already exist in the backend
  const shippingAddress = event.value.shipping
  if (shippingAddress && !shippingAddress._id) {
    const address = await customerStore.saveAddress(shippingAddress, rogue)
    shippingAddress._id = address._id
  }

  // Dont set / update password unless the customer specifically tried to do so
  if (!event.value.hasPassword) {
    event.value.password = undefined
  }

  try {
    // Metadata that will be saved with this list
    const registry = JSON.parse(JSON.stringify(event.value)) as RegistryEvent
    const metadata = { isActive: true, registry } as unknown as List['metadata']
    const recipient = event.value.recipients[0]
    if (!hasAdditionalRecipient.value) {
      registry.recipients = [recipient]
    }
    const address = registry.shipping as DisplayAddress
    address.telephone = address.telephone || recipient.phone as string
    address.email = address.email || recipient.email
    address.registry = { name: event.value.name }

    event.value.password = ''
    if (props.editListId) {
      // Remove the password
      await listStore.updateMetadata(rogue, props.editListId, metadata)
      emit('save', { _id: props.editListId })
    } else {
      const list = await rogue.fetch<List>('/svc_list/lists', 'POST', { type: 'registry', metadata })

      listStore.addList(rogue, list)
      emit('save', list)
    }
  } catch (e) {
    rogue.log.warn('Error creating registry', e)
  }
  loading.value = false
}

if (editList.value) {
  const savedRegistry = editList.value.metadata?.registry as unknown as RegistryEvent

  Object.assign(event.value, savedRegistry)
  hasAdditionalRecipient.value = event.value.recipients.length > 1
  if (event.value.recipients.length === 1) {
    event.value.recipients.push({ firstName: '', lastName: '', email: '' })
  }
  addressId.value = savedRegistry.shipping?._id || ''
  netHasPass.value = Boolean(savedRegistry.hasPassword)
} else {
  Object.assign(event.value.recipients[0], {
    firstName: firstname.value, lastName: lastname.value, email: email.value, phone: (addresses.value[0] as Address & { phone?: string })?.phone,
  })
  hasAdditionalRecipient.value = false
}

onMounted(() => {
  selectShipping(shipping.value[0].value as string)
})
</script>
<style lang="scss" scoped>
.password-check, .change-pass {
  margin-bottom: $space-s;
}

.change-pass {
  font-size: $size-xs;
}

.delete {
  margin-bottom: $space-s;
}

.extra-recipient {
  margin: $space-s 0;
}

.flex {
  display: flex;
  justify-content: center;

  .button {
    margin: 0 $space-xs;
  }
}

</style>
